import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

// Lançamentos contábeis gravados no banco (migração 028), no mesmo formato dos
// JSONs de src/data/empresas/<slug>/lanc-*.json, para entrarem em
// lancamentosContabeis junto com os arquivos.
//
// Convivência com os JSONs: contabil_empresas.inicio_lancamentos é a data de
// corte. Só vale da tabela o que for >= essa data (o gatilho do banco já recusa
// o contrário; o filtro aqui é defesa extra contra duplicar o período dos JSONs).
//
// Se a tabela ainda não existir (migração não aplicada), se o RLS negar ou se a
// rede falhar, devolve lista vazia e registra no console: a tela segue só com os
// JSONs, exatamente como antes.

export const CONTA_A_CLASSIFICAR = '9.9.9 — A Classificar';

const VAZIO = Object.freeze([]);
const PAGINA = 1000; // teto padrão de linhas por resposta do PostgREST

function paraFormatoJson(row) {
  return {
    id: `db-${row.id}`,
    data: row.data,
    historico: row.historico,
    contaDebito: row.conta_debito || CONTA_A_CLASSIFICAR,
    contaCredito: row.conta_credito || CONTA_A_CLASSIFICAR,
    valor: Number(row.valor),
    documento: row.documento || '',
    centroCusto: row.centro_custo || '',
    origem: row.origem || 'manual',
    // Marcas internas: o lançamento veio da tabela, não do JSON.
    _db: true,
    _dbId: row.id,
    _extratoId: row.extrato_id || null,
    // Para saber se a conta mudou depois de validada (fechamento do mês).
    _criadoEm: row.criado_em || null,
    _atualizadoEm: row.atualizado_em || null,
  };
}

async function buscarTodos(slug) {
  const todos = [];
  for (let de = 0; ; de += PAGINA) {
    const { data, error } = await supabase
      .from('contabil_lancamentos')
      .select('id, data, historico, conta_debito, conta_credito, valor, documento, centro_custo, origem, extrato_id, criado_em, atualizado_em')
      .eq('empresa_slug', slug)
      .order('data', { ascending: true })
      .order('criado_em', { ascending: true })
      .order('id', { ascending: true })
      .range(de, de + PAGINA - 1);
    if (error) throw error;
    todos.push(...(data || []));
    if (!data || data.length < PAGINA) break;
  }
  return todos;
}

export function useContabilLancamentos(slug) {
  const [estado, setEstado] = useState({ slug: null, inicio: null, lancamentos: VAZIO, erro: null, carregando: false });
  const seqRef = useRef(0);

  const carregar = useCallback(async () => {
    if (!slug) return;
    const seq = ++seqRef.current;
    setEstado(prev => ({ ...prev, carregando: true }));
    try {
      const { data: emp, error: errEmp } = await supabase
        .from('contabil_empresas')
        .select('inicio_lancamentos')
        .eq('empresa_slug', slug)
        .maybeSingle();
      if (errEmp) throw errEmp;
      const inicio = emp?.inicio_lancamentos || null;
      // Sem data de corte cadastrada não há como saber onde termina o JSON:
      // nada da tabela entra, para não arriscar duplicar.
      const rows = inicio ? await buscarTodos(slug) : [];
      if (seq !== seqRef.current) return; // troca de empresa no meio do caminho
      const lancamentos = rows.filter(r => r.data >= inicio).map(paraFormatoJson);
      setEstado({ slug, inicio, lancamentos, erro: null, carregando: false });
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error('[contabilidade] Falha ao carregar lançamentos do banco; seguindo só com os arquivos JSON.', err);
      setEstado({ slug, inicio: null, lancamentos: VAZIO, erro: err, carregando: false });
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return undefined;
    carregar();
    let timer = null;
    const recarregar = () => { clearTimeout(timer); timer = setTimeout(carregar, 400); };
    const canal = supabase.channel(`contabil-lancamentos-${slug}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contabil_lancamentos', filter: `empresa_slug=eq.${slug}` }, recarregar)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contabil_empresas', filter: `empresa_slug=eq.${slug}` }, recarregar)
      .subscribe();
    return () => { clearTimeout(timer); supabase.removeChannel(canal); };
  }, [slug, carregar]);

  // Enquanto a empresa nova não carregou, não devolve dados da anterior.
  const atual = estado.slug === slug;
  return {
    inicioLancamentos: atual ? estado.inicio : null,
    lancamentosDb: atual ? estado.lancamentos : VAZIO,
    erro: atual ? estado.erro : null,
    carregando: estado.carregando || !atual,
    recarregar: carregar,
  };
}

export default useContabilLancamentos;
